/**
 * Pane 1 — the Funding Statement: the deliverable this loan funds at.
 *
 * Renders the contract in `app/models/funding_statements.py` structurally rather
 * than as generic key/value rows, because the model has a real shape:
 *
 *   FundingStatementBase — loan header + terms, then `titleLines[]` (the Title
 *     column: escrows, per-diem, fees, reserves, holdback) building down to the
 *     computed `netWireToTitle` (totalLoanAmount − Σ lines).
 *   TableFundingStatement — adds a SECOND audience: `correspondentLines[]` and
 *     `netWireToCorrespondent`. TWO net wires. That is why the ledger below
 *     renders as two side-by-side columns on a TFS and one on an LFS — it is the
 *     model's own structure, not a layout preference.
 *   StatementLifecycle — Projection → Locked → Acknowledged, which drives the
 *     button set: Lock only while Projection (closer-gated, the one write that
 *     changes the statement's nature), Acknowledge only once Locked.
 *
 * The FE never computes a statement figure — statements are recomputed by the
 * backend from the loan on every read until the closer locks (the "projections"
 * rule in WIRE_AND_FUNDING_TAB.md), so echoing a derived number back would rot
 * the snapshot.
 */
import { useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";

import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import InputWrapper from "../../../../../components/inputs/shared/InputWrapper";
import { formatDate } from "../../../../../_src_shared/utils/functions/formatDate";
import { useIsAcquisitions } from "../../../../../pipelines/PipelineContext";
import { AdaptiveRows, Disclosure, leftoverKeys } from "../adaptiveDetails";
import {
  EMPTY,
  EmptyLine,
  LineRow,
  Pill,
  SectionHead,
  StatCell,
  StatStrip,
  T,
  labelSx,
  money,
  num,
  ratePct,
  signedMoney,
} from "../wireFundingShared";
import {
  PaneEmpty,
  PaneHeader,
  PaneLoading,
  PaneShell,
  PaneStrip,
  StripCell,
} from "./PaneShell";

const LIFECYCLE = ["Projection", "Locked", "Acknowledged"];

/** The Projection → Locked → Acknowledged track, with who/when under each stop. */
const LifecycleTrack = ({ statement }) => {
  const value = statement?.statementStatus;
  const activeIndex = Math.max(0, LIFECYCLE.indexOf(value));

  const captions = [
    statement?.generatedDate
      ? `generated ${formatDate(statement.generatedDate, "", "MMM d, yyyy")}`
      : "recomputed on every read",
    statement?.lockedAt
      ? `${statement.lockedBy || "closer"} · ${formatDate(statement.lockedAt, "", "MMM d, yyyy")}`
      : "closer freezes + files the PDF",
    statement?.acknowledgedAt
      ? `${statement.acknowledgedBy || "counterparty"} · ${formatDate(statement.acknowledgedAt, "", "MMM d, yyyy")}`
      : "counterparty countersigns",
  ];

  return (
    <Flx ac sx={{ width: "100%" }}>
      {LIFECYCLE.map((stage, i) => {
        const reached = i <= activeIndex;
        const isCurrent = i === activeIndex;
        return (
          <Flx
            key={stage}
            ac
            gap={1}
            sx={{ flex: i === LIFECYCLE.length - 1 ? "none" : 1, minWidth: 0 }}
          >
            <Flx ac gap={0.9} sx={{ flex: "none" }}>
              <Box
                sx={{
                  width: 13,
                  height: 13,
                  borderRadius: "50%",
                  flex: "none",
                  border: `${isCurrent ? 4 : 1.5}px solid ${reached ? T.accent : T.line}`,
                  bgcolor: reached && !isCurrent ? T.accent : T.card,
                }}
              />
              <Flx column gap={0.1}>
                <Txt
                  sx={{
                    fontSize: 12,
                    fontWeight: isCurrent ? 700 : 500,
                    color: isCurrent ? T.accentInk : reached ? T.ink2 : T.faint,
                    whiteSpace: "nowrap",
                    lineHeight: 1.25,
                  }}
                >
                  {stage}
                </Txt>
                <Txt
                  sx={{
                    fontSize: 10.5,
                    color: T.faint,
                    whiteSpace: "nowrap",
                    lineHeight: 1.3,
                  }}
                >
                  {captions[i]}
                </Txt>
              </Flx>
            </Flx>
            {i < LIFECYCLE.length - 1 ? (
              <Box
                sx={{
                  flex: 1,
                  minWidth: 16,
                  height: "1.5px",
                  bgcolor: i < activeIndex ? T.accent : T.line,
                }}
              />
            ) : null}
          </Flx>
        );
      })}
    </Flx>
  );
};

// StatementLine.category → badge tone (the model's bucket vocabulary:
// escrow | per_diem | fee | reserve | holdback | premium | strip | passthrough).
const CATEGORY_TONE = {
  escrow: "info",
  per_diem: "neutral",
  fee: "warning",
  reserve: "info",
  holdback: "accent",
  premium: "success",
  strip: "success",
  passthrough: "neutral",
};

export const categoryBadge = (category) => {
  if (!category) return {};
  return {
    badge: String(category).replace(/_/g, " ").toUpperCase(),
    badgeTone: CATEGORY_TONE[String(category).toLowerCase()] || "neutral",
  };
};

/**
 * One audience column: a build-down from an opening balance through its lines
 * to a net wire. Title lines SUBTRACT (netWireToTitle = totalLoanAmount − Σ),
 * correspondent lines SUM (netWireToCorrespondent = Σ) — so each column states
 * its own arithmetic rather than sharing one guessed sign convention.
 */
const AudienceColumn = ({
  label,
  opening,
  openingLabel,
  lines,
  total,
  totalLabel,
  subtract,
  emptyText,
}) => (
  <Box sx={{ minWidth: 0 }}>
    <SectionHead
      label={label}
      end={`${lines.length} ${lines.length === 1 ? "line" : "lines"}`}
    />
    {opening != null ? (
      <LineRow label={openingLabel} amount={money(opening)} />
    ) : null}
    {lines.length ? (
      lines.map((line, i) => {
        const amount = num(line?.amount);
        const effect = subtract ? -amount : amount;
        const rawLabel = line?.label || "Line item";
        const prefixed =
          subtract && !/^(less|plus)\b/i.test(rawLabel)
            ? `${amount < 0 ? "Plus" : "Less"}: ${rawLabel}`
            : rawLabel;
        return (
          <LineRow
            key={line?.oid || `${rawLabel}-${i}`}
            label={prefixed}
            {...categoryBadge(line?.category)}
            amount={signedMoney(effect)}
            note={line?.calcNote}
            tone={effect < 0 ? T.ink2 : T.green}
          />
        );
      })
    ) : (
      <EmptyLine>{emptyText}</EmptyLine>
    )}
    <LineRow
      strong
      label={totalLabel}
      badge="WIRE"
      badgeTone="accent"
      amount={money(total)}
    />
  </Box>
);

// Rendered structurally above; anything else falls to System details.
const RENDERED_KEYS = new Set([
  "statementStatus",
  "statementKind",
  "statementType",
  "documentOid",
  "lockedBy",
  "lockedAt",
  "acknowledgedBy",
  "acknowledgedAt",
  "acknowledgmentMethod",
  "sellerSignature",
  "sellerSignedDate",
  "oid",
  "_id",
  "dealId",
  "syncLoanId",
  "generatedDate",
  "loanProductType",
  "originator",
  "propertyAddresses",
  "baseLoanAmount",
  "financedRehabAmount",
  "totalLoanAmount",
  "titleLines",
  "netWireToTitle",
  "correspondentLines",
  "netWireToCorrespondent",
  "noteRate",
  "rateTypeLabel",
  "prepaymentPenaltyStructure",
  "amortizationType",
  "expectedClosingDate",
  "firstPaymentDate",
  "buyRate",
  "interestStripRate",
  "dscrBuyPrice",
  "fundingType",
]);

const StatementPane = ({
  statement,
  loading,
  failed,
  isCloser,
  onViewPdf,
  onLock,
  locking,
  onAcknowledge,
  acknowledging,
}) => {
  const [ackName, setAckName] = useState("");
  // The statement only carries the loan's expectedClosingDate. An acquisition
  // settles a trade, so its date is the loan's expected settlement instead.
  const isAcquisition = useIsAcquisitions();
  const expectedSettlementDate = useSelector(
    (state) => state.loanDrilldown?.expectedSettlementDate,
  );

  const status = statement?.statementStatus;
  const kind = statement?.statementKind || statement?.statementType;
  const isLocked = status === "Locked" || status === "Acknowledged";
  const isTfs = kind === "TFS";

  const titleLines = Array.isArray(statement?.titleLines)
    ? statement.titleLines
    : [];
  const correspondentLines = Array.isArray(statement?.correspondentLines)
    ? statement.correspondentLines
    : [];

  const header = (
    <PaneHeader
      title="Funding Statement"
      badges={
        <>
          {kind ? <Pill label={kind} tone="accent" /> : null}
          {status ? (
            <Pill
              label={status}
              tone={
                status === "Acknowledged"
                  ? "success"
                  : status === "Locked"
                    ? "warning"
                    : "info"
              }
            />
          ) : null}
        </>
      }
      subtitle={
        isTfs
          ? "Table funding — two audiences, two net wires; the correspondent countersigns."
          : "Direct origination — one net wire to title; the lock is the terminal state."
      }
      actions={
        <>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PictureAsPdfOutlinedIcon />}
            onClick={onViewPdf}
            disabled={!statement}
          >
            View PDF
          </Button>
          {isCloser && status === "Projection" ? (
            <Button
              variant="contained"
              size="small"
              startIcon={<LockOutlinedIcon />}
              onClick={onLock}
              disabled={locking}
            >
              {locking ? "Locking…" : "Lock statement"}
            </Button>
          ) : null}
        </>
      }
    />
  );

  if (loading) return <PaneShell header={header}><PaneLoading /></PaneShell>;
  if (failed || !statement) {
    return (
      <PaneShell header={header}>
        <PaneEmpty>
          No funding statement is available for this loan yet. The backend
          generates one from the loan&apos;s <code>fundingType</code> — check
          that the loan has a funding channel set.
        </PaneEmpty>
      </PaneShell>
    );
  }

  const terms = [
    {
      label: "Note rate",
      value: ratePct(statement.noteRate),
      sub: statement.rateTypeLabel || null,
      mono: true,
    },
    {
      label: "Amortization",
      value: statement.amortizationType || "not set",
    },
    {
      label: "Prepay structure",
      value: statement.prepaymentPenaltyStructure || EMPTY,
      mono: true,
    },
    isAcquisition
      ? {
          label: "Expected settlement",
          value: formatDate(expectedSettlementDate, EMPTY, "MMM d, yyyy"),
        }
      : {
          label: "Expected closing",
          value: formatDate(statement.expectedClosingDate, EMPTY, "MMM d, yyyy"),
        },
    {
      label: "First payment",
      value: formatDate(statement.firstPaymentDate, EMPTY, "MMM d, yyyy"),
    },
    {
      label: "Base loan",
      value: money(statement.baseLoanAmount),
      mono: true,
    },
    ...(statement.financedRehabAmount
      ? [
          {
            label: "Financed rehab",
            value: money(statement.financedRehabAmount),
            mono: true,
            sub: "draw-funded holdback",
          },
        ]
      : []),
    {
      label: "Total loan",
      value: money(statement.totalLoanAmount),
      mono: true,
    },
  ];

  // TFS economics are product-conditional on the model (RTL carries the strip,
  // DSCR carries the buy price), so only the populated ones render.
  const economics = [
    statement.buyRate != null && {
      label: "Buy rate",
      value: ratePct(statement.buyRate),
      sub: "aggregator net coupon",
    },
    statement.interestStripRate != null && {
      label: "Interest strip",
      value: ratePct(statement.interestStripRate),
      sub: "note rate − buy rate",
    },
    statement.dscrBuyPrice != null && {
      label: "DSCR buy price",
      value: `${statement.dscrBuyPrice}`,
      sub: "points, 100 = par",
    },
  ].filter(Boolean);

  const addresses = Array.isArray(statement.propertyAddresses)
    ? statement.propertyAddresses
    : [];

  const systemDetails = leftoverKeys(statement, RENDERED_KEYS);

  return (
    <PaneShell
      header={header}
      strip={
        <PaneStrip meta="Live — recomputed from current loan terms on each read">
          <StripCell
            label="State"
            value={status || "Not generated"}
            sub={
              statement.generatedDate
                ? `generated ${formatDate(statement.generatedDate, "", "MMM d, yyyy")}`
                : null
            }
          />
          <StripCell
            label="Net wire to title"
            value={money(statement.netWireToTitle)}
          />
          <StripCell
            label="Total loan"
            value={money(statement.totalLoanAmount)}
          />
        </PaneStrip>
      }
    >
      <Flx column gap={2.5} fw>
        <LifecycleTrack statement={statement} />

        {status === "Projection" ? (
          <Txt sx={{ fontSize: 12, color: T.muted, fontStyle: "italic" }}>
            Live projection — every figure below is recomputed from current loan
            terms on each read. Locking freezes the snapshot and files the PDF
            into the Closing Package.
          </Txt>
        ) : null}

        <StatStrip>
          {terms.map((t) => (
            <StatCell key={t.label} {...t} />
          ))}
        </StatStrip>

        {economics.length ? (
          <Box>
            <SectionHead label="Table funding economics" />
            <StatStrip sx={{ mt: 1.25, bgcolor: T.accentTint }}>
              {economics.map((e) => (
                <StatCell key={e.label} mono {...e} />
              ))}
            </StatStrip>
          </Box>
        ) : null}

        {/* The ledger. Two audiences on a TFS, one on an LFS — the model's
            own shape, so the layout follows it. */}
        <Box
          sx={{
            display: "grid",
            gap: 3,
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              ...(isTfs || correspondentLines.length
                ? { lg: "repeat(2, minmax(0, 1fr))" }
                : {}),
            },
          }}
        >
          <AudienceColumn
            label="Title column — funds the loan"
            opening={statement.totalLoanAmount}
            openingLabel="Total loan amount"
            lines={titleLines}
            total={statement.netWireToTitle}
            totalLabel="Net wire to title"
            subtract
            emptyText="No title lines — nothing is withheld from the wire."
          />
          {isTfs || correspondentLines.length ? (
            <AudienceColumn
              label="Correspondent column — pays the seller"
              lines={correspondentLines}
              total={statement.netWireToCorrespondent}
              totalLabel="Net wire to correspondent"
              emptyText="No correspondent lines on this statement yet."
            />
          ) : null}
        </Box>

        {addresses.length ? (
          <Box>
            <SectionHead
              label="Subject properties"
              end={`${addresses.length}`}
            />
            <Flx column gap={0.25} sx={{ pt: 0.75 }}>
              {addresses.map((a, i) => (
                <Txt key={`${a}-${i}`} sx={{ fontSize: 12.5, color: T.ink2 }}>
                  {a}
                </Txt>
              ))}
            </Flx>
          </Box>
        ) : null}

        {/* Acknowledgment — the backend 409s unless something is locked, so the
            block only appears in the one state where the POST can succeed. */}
        {isLocked && status !== "Acknowledged" ? (
          <Box sx={{ borderTop: `1px solid ${T.line}`, pt: 2 }}>
            <Txt sx={{ ...labelSx, display: "block", mb: 1.25 }}>
              Counterparty acknowledgment
            </Txt>
            <Flx gap={1.5} wrap sx={{ alignItems: "flex-end" }}>
              <InputWrapper
                label="Acknowledged by"
                sx={{ minWidth: 280, flexGrow: 1, maxWidth: 420 }}
                labelSx={{ fontSize: 12.5, color: T.ink2 }}
              >
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Signatory name"
                  value={ackName}
                  onChange={(e) => setAckName(e.target.value)}
                />
              </InputWrapper>
              <Button
                variant="contained"
                onClick={() => {
                  if (!ackName.trim()) return;
                  onAcknowledge(ackName.trim(), () => setAckName(""));
                }}
                disabled={acknowledging || !ackName.trim()}
                sx={{ flex: "none" }}
              >
                {acknowledging ? "Submitting…" : "Acknowledge"}
              </Button>
            </Flx>
            <Txt sx={{ fontSize: 11, color: T.faint, mt: 1 }}>
              Recorded as a countersigned-PDF acknowledgment
              (<code>method: signed_pdf</code>). The portal e-sign path is future
              state.
            </Txt>
          </Box>
        ) : null}

        {status === "Acknowledged" ? (
          <Flx
            ac
            gap={1.25}
            sx={{
              px: 1.75,
              py: 1.5,
              borderRadius: T.radiusSm,
              bgcolor: T.greenTint,
              border: `1px solid #CBE8D6`,
            }}
          >
            <CheckCircleRounded sx={{ fontSize: 18, color: T.green }} />
            <Txt sx={{ fontSize: 12.5, color: T.green, fontWeight: 600 }}>
              Acknowledged
              {statement.acknowledgedBy ? ` by ${statement.acknowledgedBy}` : ""}
              {statement.acknowledgedAt
                ? ` · ${formatDate(statement.acknowledgedAt, "", "MMM d, yyyy")}`
                : ""}
              {statement.acknowledgmentMethod
                ? ` · ${statement.acknowledgmentMethod.replace(/_/g, " ")}`
                : ""}
            </Txt>
          </Flx>
        ) : null}

        {systemDetails ? (
          <Disclosure label="System details">
            <AdaptiveRows data={systemDetails} />
          </Disclosure>
        ) : null}
      </Flx>
    </PaneShell>
  );
};

StatementPane.propTypes = {
  statement: PropTypes.object,
  loading: PropTypes.bool,
  failed: PropTypes.bool,
  isCloser: PropTypes.bool,
  onViewPdf: PropTypes.func,
  onLock: PropTypes.func,
  locking: PropTypes.bool,
  onAcknowledge: PropTypes.func,
  acknowledging: PropTypes.bool,
};

export default StatementPane;
